import React, { useEffect, useRef, useState } from "react";
import { skuApi, templateApi, apiError } from "../../api/lab";
import { useApp } from "../../AppProvider";
import { Modal, Button, Field, Badge } from "../../Components/UI/ui";
import { SKU_KINDS, SERVICE_TYPES, MODALITIES, cap } from "../../lib/lab";

const blank = {
  name: "", kind: "test", serviceType: "pathology", category: "", modality: "",
  unitPrice: "", cgst: "", sgst: "", igst: "",
  specimenType: "", containerType: "", turnaroundHours: "", preparationNotes: "",
  homeCollectionAvailable: false, description: "",
};

// Create / edit one catalogue entry (§2). Price changes are versioned server-side.
const SkuForm = ({ open, onClose, sku, categories = [], onSaved, defaults, lockKind = false }) => {
  const { toast, invalidateSkus, templates, loadTemplates } = useApp();
  const [f, setF] = useState(blank);
  // Test definitions linked to this SKU (LabSku.templateIds) — what a booking
  // of it produces as result sheets. Managed via the template link endpoints.
  const [linked, setLinked] = useState([]);
  const [addTpl, setAddTpl] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  // `defaults` is usually an inline object; read it via a ref so the form only resets on open
  const defaultsRef = useRef(defaults); defaultsRef.current = defaults;

  useEffect(() => {
    if (!open) return;
    setErr(null);
    loadTemplates();
    setLinked((sku?.templateIds || []).map(String));
    setAddTpl("");
    setF(sku ? {
      ...blank,
      name: sku.name || "", kind: sku.kind || "test", serviceType: sku.serviceType || "pathology",
      category: sku.category || "", modality: sku.modality || "",
      unitPrice: sku.unitPrice ?? "", cgst: sku.tax?.cgst ?? "", sgst: sku.tax?.sgst ?? "", igst: sku.tax?.igst ?? "",
      specimenType: sku.specimenType || "", containerType: sku.containerType || "",
      turnaroundHours: sku.turnaroundHours ?? "", preparationNotes: sku.preparationNotes || "",
      homeCollectionAvailable: !!sku.homeCollectionAvailable, description: sku.description || "",
    } : { ...blank, ...(defaultsRef.current || {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sku]);

  const submit = async () => {
    setErr(null);
    if (!f.name.trim()) return setErr("Name is required");
    if (f.unitPrice === "" || Number(f.unitPrice) < 0) return setErr("Unit price is required");
    const body = {
      name: f.name.trim(),
      kind: f.kind,
      serviceType: f.serviceType,
      category: f.category.trim() || undefined,
      unitPrice: Number(f.unitPrice),
      tax: { cgst: Number(f.cgst) || 0, sgst: Number(f.sgst) || 0, igst: Number(f.igst) || 0 },
      turnaroundHours: f.turnaroundHours === "" ? undefined : Number(f.turnaroundHours),
      preparationNotes: f.preparationNotes.trim(),
      description: f.description.trim(),
      homeCollectionAvailable: f.serviceType === "pathology" ? !!f.homeCollectionAvailable : false,
    };
    if (f.serviceType === "pathology") { body.specimenType = f.specimenType.trim(); body.containerType = f.containerType.trim(); }
    if (f.kind === "scan" && f.modality) body.modality = f.modality;
    setSaving(true);
    try {
      const saved = sku ? await skuApi.update(sku._id, body) : await skuApi.create(body);
      const skuId = saved?._id || sku?._id;
      // Reconcile template links: add the new ones, drop the removed ones
      const before = new Set((sku?.templateIds || []).map(String));
      const after = new Set(hasSheets ? linked : []);
      const linkErrors = [];
      for (const id of after) if (!before.has(id)) { try { await templateApi.linkSku(id, skuId); } catch (e) { linkErrors.push(apiError(e)); } }
      for (const id of before) if (!after.has(id)) { try { await templateApi.unlinkSku(id, skuId); } catch (e) { linkErrors.push(apiError(e)); } }
      invalidateSkus();
      if (linkErrors.length) toast.error(`Saved, but linking failed: ${linkErrors[0]}`);
      else toast.success(sku ? "SKU updated" : "SKU created");
      onSaved?.({ ...saved, templateIds: [...after] });
      onClose();
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Only pathology items produce result sheets; radiology reports are attached separately
  const hasSheets = f.serviceType === "pathology" && f.kind !== "service";
  const isPackage = f.kind === "package";
  const tplList = Object.values(templates).sort((a, b) => a.name.localeCompare(b.name));
  const tplLabel = (t) => `${t.name}${t.shortName ? ` (${t.shortName})` : ""}${t.category ? ` · ${t.category}` : ""}${t.isActive === false ? " · inactive" : ""}`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={sku ? `Edit · ${sku.name}` : f.kind === "package" ? "New package" : "New catalogue item"}
      footer={
        <>
          {err && <span className="lab-danger-text" style={{ marginRight: "auto" }}>{err}</span>}
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={submit}>{sku ? "Save" : "Create"}</Button>
        </>
      }
    >
      <div className="lab-grid cols-2">
        <Field label="Name"><input className="lab-input" value={f.name} onChange={(e) => set({ name: e.target.value })} autoFocus /></Field>
        <Field label="Category" hint="Used for grouping, e.g. Biochemistry">
          <input className="lab-input" list="sku-categories" value={f.category} onChange={(e) => set({ category: e.target.value })} />
          <datalist id="sku-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
        </Field>
        <Field label="Service type">
          <select className="lab-select" value={f.serviceType} onChange={(e) => set({ serviceType: e.target.value, kind: e.target.value === "radiology" && f.kind === "test" ? "scan" : f.kind })}>
            {SERVICE_TYPES.map((t) => <option key={t} value={t}>{cap(t)}</option>)}
          </select>
        </Field>
        <Field label="Kind">
          <select className="lab-select" value={f.kind} disabled={lockKind} onChange={(e) => set({ kind: e.target.value })}>
            {SKU_KINDS.map((k) => <option key={k} value={k}>{cap(k)}</option>)}
          </select>
        </Field>
        {f.kind === "scan" && (
          <Field label="Modality">
            <select className="lab-select" value={f.modality} onChange={(e) => set({ modality: e.target.value })}>
              <option value="">—</option>
              {MODALITIES.map((m) => <option key={m}>{m}</option>)}
            </select>
          </Field>
        )}
        <Field label="Unit price (₹)"><input type="number" min="0" step="0.01" className="lab-input" value={f.unitPrice} onChange={(e) => set({ unitPrice: e.target.value })} /></Field>
        <Field label="Turnaround (hours)"><input type="number" min="0" className="lab-input" value={f.turnaroundHours} onChange={(e) => set({ turnaroundHours: e.target.value })} /></Field>
      </div>

      <div className="lab-grid cols-3">
        <Field label="CGST %"><input type="number" min="0" max="100" className="lab-input" value={f.cgst} onChange={(e) => set({ cgst: e.target.value })} /></Field>
        <Field label="SGST %"><input type="number" min="0" max="100" className="lab-input" value={f.sgst} onChange={(e) => set({ sgst: e.target.value })} /></Field>
        <Field label="IGST %"><input type="number" min="0" max="100" className="lab-input" value={f.igst} onChange={(e) => set({ igst: e.target.value })} /></Field>
      </div>

      {f.serviceType === "pathology" && (
        <div className="lab-grid cols-3">
          <Field label="Specimen"><input className="lab-input" placeholder="Blood" value={f.specimenType} onChange={(e) => set({ specimenType: e.target.value })} /></Field>
          <Field label="Container"><input className="lab-input" placeholder="EDTA" value={f.containerType} onChange={(e) => set({ containerType: e.target.value })} /></Field>
          <Field label="Home collection">
            <label className="lab-check" style={{ height: 40 }}>
              <input type="checkbox" checked={f.homeCollectionAvailable} onChange={(e) => set({ homeCollectionAvailable: e.target.checked })} /> Available
            </label>
          </Field>
        </div>
      )}

      {hasSheets && (
        <div className="lab-section">
          <h4>
            Result sheet definition{isPackage ? "s" : ""}
            <span className="lab-muted" style={{ fontWeight: 400 }}>{isPackage ? "one sheet per test in the package" : "what a booking of this item produces"}</span>
          </h4>
          {isPackage ? (
            <>
              {linked.length === 0 && <div className="lab-muted" style={{ marginBottom: 8 }}>No tests yet — a booking of this package would produce no result sheets.</div>}
              <div className="lab-row" style={{ marginBottom: linked.length ? 10 : 0 }}>
                {linked.map((id) => {
                  const t = templates[id];
                  return (
                    <span key={id} className="lab-tag">
                      {t ? tplLabel(t) : id}
                      <button type="button" className="lab-link" style={{ marginLeft: 6 }} onClick={() => setLinked(linked.filter((x) => x !== id))}>✕</button>
                    </span>
                  );
                })}
              </div>
              <div className="lab-row">
                <select className="lab-select" style={{ flex: 1 }} value={addTpl} onChange={(e) => setAddTpl(e.target.value)}>
                  <option value="">Add a test definition…</option>
                  {tplList.filter((t) => !linked.includes(t._id)).map((t) => <option key={t._id} value={t._id}>{tplLabel(t)}</option>)}
                </select>
                <Button size="sm" disabled={!addTpl} onClick={() => { setLinked([...linked, addTpl]); setAddTpl(""); }}>Add</Button>
              </div>
            </>
          ) : (
            <div className="lab-row">
              <select className="lab-select" style={{ flex: 1 }} value={linked[0] || ""} onChange={(e) => setLinked(e.target.value ? [e.target.value] : [])}>
                <option value="">None — no result sheet (bookings will be flagged)</option>
                {tplList.map((t) => <option key={t._id} value={t._id}>{tplLabel(t)}</option>)}
              </select>
              {linked.length > 1 && <Badge kind="IN_PROCESS">{linked.length} linked</Badge>}
            </div>
          )}
          {linked.length > 1 && !isPackage && (
            <div className="lab-muted" style={{ marginTop: 6 }}>This item currently has {linked.length} definitions linked; picking one above keeps only that one.</div>
          )}
          {tplList.length === 0 && <div className="lab-muted" style={{ marginTop: 6 }}>No test definitions exist yet — create them under Tests.</div>}
        </div>
      )}

      <div className="lab-grid cols-2">
        <Field label="Preparation notes" hint="Shown to the patient, e.g. 12h fasting"><textarea className="lab-textarea" value={f.preparationNotes} onChange={(e) => set({ preparationNotes: e.target.value })} /></Field>
        <Field label="Description"><textarea className="lab-textarea" value={f.description} onChange={(e) => set({ description: e.target.value })} /></Field>
      </div>
    </Modal>
  );
};

export default SkuForm;
